import React, { useState } from 'react';
import { Card, Badge, Row, Col, Button } from 'react-bootstrap';
import { format } from 'date-fns';
import IconShow from "../../../images/fi_eye.png";
import IconEdit from "../../../images/edit.png";
import './KanbanView.css';
import { badgePaiement } from './badgePaiement';
import { manutentionResume } from './manutentionResume';
import { totalTtcCommande, detailTotalCommande } from './totalCommande';

const KanbanView = ({ orders, onStatusChange, totalDelivered, loadAllDelivered, onLoadAllDelivered }) => {
  const [draggedOrder, setDraggedOrder] = useState(null);

  // Définition des colonnes du Kanban par statut
  const columns = [
    { id: 4, title: 'En cours de validation', color: 'primary', icon: '✓' },
    { id: 3, title: 'En préparation', color: 'info', icon: '📦' },
    { id: 1, title: 'En cours de livraison', color: 'warning', icon: '🚚' },
    { id: 2, title: 'Livré', color: 'success', icon: '✅' },
  ];

  // Le retard vient de l'API (RetardLivraisonService) : échéance = fin du
  // créneau promis + manutention prévue, comparée à la livraison réelle ou à
  // maintenant. Même règle que la liste et les récaps. Le calcul local ne sert
  // plus que si l'API n'est pas encore à jour.
  const isOrderLate = (order) => {
    if (order.retard_livraison) return order.retard_livraison.en_retard;
    if (!order.DateLivraison) return false;

    const deliveryPlanned = new Date(order.DateLivraison);

    // Si la commande est livrée, comparer avec la date de livraison réelle
    // On ajoute 30 minutes de marge à la date prévue (temps de déchargement)
    if (order.StatuCommande === 2 && order.DateLivraisonReelle) {
      const deliveryReal = new Date(order.DateLivraisonReelle);
      // Ajouter 30 minutes de marge à la date prévue
      const deliveryPlannedWithBuffer = new Date(deliveryPlanned.getTime() + 30 * 60 * 1000);
      // En retard seulement si la livraison réelle dépasse la date prévue + 30 min
      return deliveryReal > deliveryPlannedWithBuffer;
    }

    // Si pas encore livrée, comparer avec la date actuelle
    const now = new Date();
    return deliveryPlanned < now;
  };

  // Calculer toutes les commandes en retard (sauf annulées et livrées)
  const lateOrders = (orders?.filter(order =>
    order.StatuCommande !== 6 && // Exclure les annulées
    order.StatuCommande !== 2 && // Exclure les livrées
    isOrderLate(order)
  ) || []).sort((a, b) => {
    return new Date(a.DateLivraison) - new Date(b.DateLivraison);
  });

  // Ajouter dynamiquement la colonne "En retard" si au moins une commande est en retard
  const displayColumns = lateOrders.length > 0
    ? [{ id: 'late', title: 'En retard', color: 'danger', icon: '🔴' }, ...columns]
    : columns;

  // Grouper les commandes par statut en excluant les commandes annulées (statut 6)
  // et trier chaque groupe avec les commandes en retard en premier
  const groupedOrders = displayColumns.reduce((acc, column) => {
    let filteredOrders;

    if (column.id === 'late') {
      // Colonne spéciale "En retard" : toutes les commandes en retard
      filteredOrders = lateOrders;
    } else {
      // Colonnes normales par statut
      filteredOrders = (orders?.filter(order =>
        order.StatuCommande === column.id && order.StatuCommande !== 6 // Exclure les commandes annulées
      ) || []).sort((a, b) => {
        // Trier : commandes en retard en premier
        const aIsLate = isOrderLate(a);
        const bIsLate = isOrderLate(b);

        if (aIsLate && !bIsLate) return -1; // a en retard, b pas en retard -> a avant b
        if (!aIsLate && bIsLate) return 1;  // a pas en retard, b en retard -> b avant a

        // Si les deux sont en retard ou pas en retard, trier par date de livraison (plus proche en premier)
        return new Date(a.DateLivraison) - new Date(b.DateLivraison);
      });
    }

    acc[column.id] = filteredOrders;
    return acc;
  }, {});

  // Gestion du drag & drop
  const handleDragStart = (e, order) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    // Ne pas permettre de déplacer vers la colonne "En retard" (colonne synthétique)
    if (newStatus === 'late') {
      setDraggedOrder(null);
      return;
    }
    if (draggedOrder && draggedOrder.StatuCommande !== newStatus) {
      // Appeler la fonction de changement de statut
      if (onStatusChange) {
        onStatusChange(draggedOrder.id, newStatus);
      }
    }
    setDraggedOrder(null);
  };

  const handleDragEnd = () => {
    setDraggedOrder(null);
  };

  return (
    <div className="kanban-board">
      <Row className="g-3">
        {displayColumns.map((column) => (
          <Col key={column.id} lg={4} md={6} sm={12}>
            <div
              className={`kanban-column border-${column.color}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* En-tête de colonne */}
              <div className={`kanban-header bg-${column.color}`}>
                <h5 className="mb-0">
                  <span className="me-2">{column.icon}</span>
                  {column.title}
                  <Badge bg="light" text="dark" className="ms-2">
                    {groupedOrders[column.id]?.length || 0}
                  </Badge>
                </h5>
              </div>

              {/* Cartes des commandes */}
              <div className="kanban-cards">
                {groupedOrders[column.id]?.length === 0 ? (
                  <div className="text-muted text-center p-3">
                    <small>Aucune commande</small>
                  </div>
                ) : (
                  <>
                  {groupedOrders[column.id]?.map((order) => {
                    const isLate = isOrderLate(order);
                    const paiement = badgePaiement(order);
                    const manutention = manutentionResume(order);
                    return (
                    <Card
                      key={order.id}
                      className={`kanban-card ${draggedOrder?.id === order.id ? 'dragging' : ''} ${isLate ? 'border-danger' : ''}`}
                      style={isLate ? { borderWidth: '2px', borderColor: '#dc3545' } : {}}
                      draggable
                      onDragStart={(e) => handleDragStart(e, order)}
                      onDragEnd={handleDragEnd}
                    >
                      <Card.Body className="p-3">
                        {/* En-tête de la carte */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center">
                            <strong className="text-primary">#{order.id}</strong>
                            {isLate && (
                              <Badge bg="danger" className="ms-2">
                                🔴 EN RETARD
                              </Badge>
                            )}
                            {/* Livreur parti chercher la marchandise, pas encore chargée :
                                le badge disparaît au passage en « en cours de livraison ». */}
                            {order.ramassage_en_route_le && ![1, 2, 6].includes(Number(order.StatuCommande)) && (
                              <Badge bg="info" className="ms-2">
                                🚚 en route enlèvement
                              </Badge>
                            )}
                          </div>
                          <div>
                            <a href={`/commande-fournisseur/show/${order.id}`} className="me-2">
                              <img src={IconShow} width="16px" alt="Voir" />
                            </a>
                            <a href={`/commande-fournisseur/edit/${order.id}`}>
                              <img src={IconEdit} width="16px" alt="Modifier" />
                            </a>
                          </div>
                        </div>

                        {/* Badge Type Client */}
                        <div className="mb-2">
                          <Badge
                            bg={
                              order.client_type === 'fournisseur'
                                ? 'primary'
                                : order.client_type === 'particulier'
                                ? 'success'
                                : order.client_type === 'entreprise'
                                ? 'warning'
                                : 'secondary'
                            }
                            className={order.client_type === 'entreprise' ? 'text-dark' : ''}
                          >
                            {order.client_type === 'fournisseur' && '🏢 Fournisseur'}
                            {order.client_type === 'particulier' && '👤 Particulier'}
                            {order.client_type === 'entreprise' && '💼 Entreprise'}
                            {!order.client_type && '❓ Inconnu'}
                          </Badge>
                          {paiement && (
                            <Badge bg={paiement.bg} text={paiement.text} className="ms-2">
                              {paiement.label}
                            </Badge>
                          )}
                          {/* Ce qui change l'organisation de la tournée : du portage,
                              et deux personnes pour un colis de plus de 40 kg. */}
                          {manutention && (
                            <Badge
                              bg={manutention.aConfirmer ? 'light' : 'warning'}
                              text="dark"
                              className={`ms-2${manutention.aConfirmer ? ' border border-warning' : ''}`}
                              title={`Manutention : ${manutention.libelle}`}
                            >
                              🏋️ Manutention{manutention.aConfirmer ? ' à confirmer' : ''}
                            </Badge>
                          )}
                          {order.manutention_deux_personnes && (
                            <Badge bg="warning" text="dark" className="ms-2" title="Colis lourd (plus de 40 kg) : deux porteurs nécessaires">
                              👥 2 livreurs
                            </Badge>
                          )}
                        </div>

                        {/* Informations de la commande */}
                        <div className="mb-2">
                          <div className="small fw-bold text-truncate">
                            {order.fournisseur_profil?.user_profil?.RaisonSocial || '—'}
                          </div>
                        </div>

                        {/* Date de livraison */}
                        <div className="small text-muted mb-2">
                          <strong>📅 Livraison:</strong>{' '}
                          {order.DateLivraison
                            ? format(new Date(order.DateLivraison), 'dd/MM/yyyy HH:mm')
                            : '—'}
                        </div>

                        {/* Villes */}
                        <div className="small text-muted">
                          <div className="d-flex justify-content-between">
                            <span>
                              📍 {order.AdressePickUp?.split(',')[1]?.trim() || '—'}
                            </span>
                            <span>→</span>
                            <span>
                              📍 {order.AdresseLivraison?.split(',')[1]?.trim() || '—'}
                            </span>
                          </div>
                        </div>

                        {/* Livreur */}
                        {order.livreur_profil && (
                          <div className="small text-muted mt-2">
                            🚚{' '}
                            {`${order.livreur_profil.nom || ''} ${order.livreur_profil.prenom || ''}`.trim()}
                          </div>
                        )}

                        {/* Tarif : total TTC transport + manutention, comme la liste.
                            La carte affichait `livraisonHT` seul, sans le dire HT, et
                            sans la manutention. */}
                        {(() => {
                          const total = totalTtcCommande(order);
                          const detail = detailTotalCommande(order);
                          if (total <= 0 && !detail) return null;
                          return (
                            <div className="small text-end mt-2">
                              <span className="fw-bold">{total.toFixed(2)} € TTC</span>
                              {detail?.aConfirmer ? (
                                <div className="text-warning" title="Manutention non chiffrée : le total n'est pas définitif">
                                  ⚠ manutention à confirmer
                                </div>
                              ) : detail ? (
                                <div className="text-muted">
                                  transport {detail.transport.toFixed(2)} · manut. {detail.manutention.toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        })()}
                      </Card.Body>
                    </Card>
                  );
                  })}
                  {/* Bouton "Charger tout" pour la colonne Livré (id: 2) */}
                  {column.id === 2 && !loadAllDelivered && totalDelivered > groupedOrders[column.id]?.length && (
                    <div className="text-center p-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={onLoadAllDelivered}
                      >
                        Charger tout ({totalDelivered} commandes)
                      </Button>
                    </div>
                  )}
                  </>
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default KanbanView;
